// The minimap: a 120 px overview strip for the Guide tab. One canvas: the hopper-weight pens (thin) over the whole history,
// the six station phase rows beneath, and a translucent rectangle for the window the main trend is showing. Redrawn at 4 Hz
// from history.queryMany; a tap reports the main trend's current window (the host decides what to do with it).
import { SIGNAL_INDEX, STATION_DEFS, PHASES } from "./sim.js"
import { PHASE_COLORS } from "./chart.js"

export const MINIMAP_H = 120
const WT_H = 88, BAND_Y = 94, ROW_H = 4

export function createMinimap(root, { history, detected = [], trend = null }) {
  const canvas = document.createElement("canvas"); canvas.className = "minimap-canvas"
  canvas.setAttribute("role", "img"); canvas.setAttribute("aria-label", "Overview of the last eight hours: hopper weights and station phases. Tap to enlarge the weight trend.")
  root.appendChild(canvas)
  const st = { view: trend ? trend.view() : null, cache: null, taps: [], destroyed: false, timer: null, w: 0 }
  const wtIdx = STATION_DEFS.map((d) => SIGNAL_INDEX[`${d.id}.wt`]), phIdx = STATION_DEFS.map((d) => SIGNAL_INDEX[`${d.id}.phase`])

  function size() {
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1
    const W = Math.max(120, Math.round(root.clientWidth || canvas.clientWidth || 320))
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(MINIMAP_H * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(MINIMAP_H * dpr) }
    canvas.style.height = `${MINIMAP_H}px`
    st.w = W
    return dpr
  }
  function query() {
    const r = history.range(), to = r.to, from = Math.max(r.from, to - 28800)
    const px = Math.max(60, Math.min(600, st.w))
    st.cache = { from, to: Math.max(to, from + 1), wt: history.queryMany(wtIdx, from, to, px), ph: history.queryMany(phIdx, from, to, px) }
  }
  const visible = () => root.clientWidth > 0   // a hidden Guide tab has no width: nothing to measure or draw
  function paint() {
    if (st.destroyed || !visible()) return
    const dpr = size(), ctx = canvas.getContext("2d"); if (!ctx) return
    const W = st.w, c = st.cache; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, MINIMAP_H)
    ctx.fillStyle = "#0b1220"; ctx.fillRect(0, 0, W, MINIMAP_H)
    if (!c) return
    const span = c.to - c.from, xOf = (t) => ((t - c.from) / span) * W
    ctx.strokeStyle = "#132033"; ctx.lineWidth = 1
    for (const kg of [0, 50, 100]) { const y = WT_H - (kg / 140) * (WT_H - 4) - 1; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }
    ctx.fillStyle = "#8ea3c0"; ctx.font = "9px monospace"; ctx.fillText("kg", 2, 10)
    const yOf = (v) => WT_H - (Math.max(0, Math.min(140, v)) / 140) * (WT_H - 4) - 1
    STATION_DEFS.forEach((d, i) => {   // the bucket's swing (min to max) as a faint column, then the max as the pen
      const q = c.wt[i]; ctx.fillStyle = d.color; ctx.globalAlpha = 0.16
      for (let k = 0; k < q.ts.length; k++) { const lo = q.min[k], hi = q.max[k]; if (lo == null || hi == null) continue; const y1 = yOf(hi), y2 = yOf(lo); ctx.fillRect(xOf(q.ts[k]), y1, Math.max(1, W / Math.max(1, q.ts.length)), Math.max(1, y2 - y1)) }
      ctx.globalAlpha = 1
    })
    STATION_DEFS.forEach((d, i) => {
      const q = c.wt[i]; ctx.strokeStyle = d.color; ctx.lineWidth = 1; ctx.beginPath()
      let pen = false
      for (let k = 0; k < q.ts.length; k++) {
        const v = q.max[k]; if (v == null || Number.isNaN(v)) { pen = false; continue }
        const x = xOf(q.ts[k]), y = WT_H - (Math.max(0, Math.min(140, v)) / 140) * (WT_H - 4) - 1
        pen ? ctx.lineTo(x, y) : ctx.moveTo(x, y); pen = true
      }
      ctx.stroke()
    })
    STATION_DEFS.forEach((d, i) => {
      const q = c.ph[i], bw = Math.max(1, W / Math.max(1, q.ts.length))
      for (let k = 0; k < q.ts.length; k++) { ctx.fillStyle = PHASE_COLORS[PHASES[Math.round(q.last[k])]] || "#000"; ctx.fillRect(xOf(q.ts[k]), BAND_Y + i * ROW_H, bw, ROW_H - 1) }
    })
    const v = st.view
    if (v) {
      const x0 = Math.max(0, xOf(v.from)), x1 = Math.min(W, v.live ? W : xOf(v.to)), w = Math.max(3, x1 - x0)
      ctx.fillStyle = "rgba(34,211,238,.16)"; ctx.fillRect(x0, 0, w, MINIMAP_H)
      ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1.5; ctx.strokeRect(x0 + .75, .75, Math.max(1, w - 1.5), MINIMAP_H - 1.5)
    }
  }
  function tick() { if (st.destroyed || !visible()) return; if (trend) st.view = trend.view(); size(); query(); paint() }

  const offView = trend ? trend.onView((v) => { st.view = v; paint() }) : null
  canvas.addEventListener("click", (e) => {
    const v = st.view; if (!v) return
    const r = canvas.getBoundingClientRect(), c = st.cache
    const t = c && r.width ? c.from + ((e.clientX - r.left) / r.width) * (c.to - c.from) : null
    for (const fn of st.taps) fn({ window: v.window, to: v.to, from: v.from, t, view: v })
  })
  tick()
  st.timer = setInterval(tick, 250)
  return {
    onTap(fn) { st.taps.push(fn); return () => { st.taps = st.taps.filter((f) => f !== fn) } },
    setView(v) { st.view = v; paint() },
    render: tick,
    destroy() { st.destroyed = true; if (st.timer != null) clearInterval(st.timer); st.timer = null; if (offView) offView(); canvas.remove() },
  }
}
